import { HttpErrorResponse } from '@angular/common/http';

import type {
  MailboxEditedMessageData,
  MailboxMessage,
  MailboxNewMessageData,
} from '@recruitee/mailbox-types';
import { CorrelationId, createEntityCollectionStore, ngrxEvent, pipeState } from '@recruitee/ngrx';

const { actions, reducer, namespace, selectors } = createEntityCollectionStore<MailboxMessage>()(
  'mailbox-messages',
  {
    // eslint-disable-next-line max-lines-per-function
    customActions: adapter => {
      const selectId = ({ id }: MailboxMessage) => id;

      return {
        send: (state, payload: { message: MailboxNewMessageData; correlationId: string }) =>
          pipeState(state, next =>
            adapter.markAsPending(next, { entities: [payload.correlationId], scope: null }),
          ),
        sendSuccess: (state, payload: { messages: MailboxMessage[]; correlationId: string }) =>
          pipeState(
            state,
            next => adapter.writeMany(next, payload.messages),
            next => adapter.markAsLoaded(next, { entities: [payload.correlationId], scope: null }),
          ),
        sendFailed: (state, payload: { error: HttpErrorResponse; correlationId: string }) =>
          adapter.setError(state, payload.error, {
            entities: [payload.correlationId],
            scope: null,
          }),

        edit: (
          state,
          payload: {
            id: number;
            correlationId: string;
            message: MailboxEditedMessageData;
          },
        ) =>
          pipeState(state, next =>
            adapter.markAsPending(next, { scope: null, entities: [payload.id] }),
          ),
        editSuccess: (state, payload: { messages: MailboxMessage[] } & CorrelationId) =>
          pipeState(
            state,
            next => adapter.writeMany(next, payload.messages),
            next =>
              adapter.markAsLoaded(next, { scope: null, entities: payload.messages.map(selectId) }),
          ),
        editFailed: (state, payload: { error: HttpErrorResponse } & CorrelationId) => state,

        delete: (state, payload: { id: number; threadId: number }) =>
          adapter.markAsPending(state, { scope: null, entities: [payload.id] }),
        deleteSuccess: (state, payload: { id: number; threadId: number }) =>
          adapter.removeOne(state, payload.id),
        deleteFailed: (state, payload: { error: HttpErrorResponse }) =>
          adapter.setError(state, payload.error),

        deliver: ngrxEvent<{ messageId: number }>(),
        deliverSuccess: ngrxEvent<{ uuid: string }>(),
        deliverFailed: ngrxEvent<{ error: HttpErrorResponse }>(),
      };
    },
  },
);

const MailboxMessagesActions = <
  Pick<
    typeof actions,
    | 'send'
    | 'sendSuccess'
    | 'sendFailed'
    | 'edit'
    | 'editSuccess'
    | 'editFailed'
    | 'delete'
    | 'deleteSuccess'
    | 'deleteFailed'
    | 'deliver'
    | 'deliverSuccess'
    | 'deliverFailed'
  >
>actions;

export {
  MailboxMessagesActions,
  actions,
  reducer as MailboxMessagesReducer,
  namespace as MailboxMessagesNamespace,
  selectors as MailboxMessagesSelectors,
};
